import { Injectable, Injector } from '@angular/core';

import { ApiService } from './api.service';
import { BaseService } from './base.service';
import { AppSettings } from './../library/app-settings';
import {
  SimpleResponse,
  ListResponse,
} from '@core/models/response-models/response-models';

import { UserGroupSearch } from '@core/models/user/user';
import { MetricSearch } from '@core/models/metric/metric';
import { AssetCategoryUpdate } from '@core/models/assets/asset';
import {
  KMTMasterResponse,
  AssetCategoryLookup,
  KMTMastersExtDTOResponse,
} from '@core/models/main-master/main-master';
import { LocationStatusGet } from '@core/models/assets/asset';
import { CurrentReservationSearch } from '@core/models/pms/pms';
import { mapValues } from 'lodash';
import {
  Optional,
  PropertyAppFeatures,
  TypedPriorityMaster,
} from '@core/models/global-entities/global-entities';
import { toOptionalNumber } from '@ui/utils/helper-util';
import { ImageUtil } from '@ui/utils/image-util';
import { APP_IMAGE_BASE64 } from '@core/models/global-entities/app-image-base64';
import { HttpResponse } from '@angular/common/http';
import {
  EntityCodes,
  TranslatedEntities,
} from '@core/models/global-entities/global-enums';
import {
  MLEntity,
  MLEntityLocale,
  MLEntityResponse,
} from '@core/models/translation/translation';
import { WOTypeMLRequest } from '@core/models/assets/kmt-asset';

@Injectable()
export class MasterDataService extends BaseService {
  public AssetCategoryTree: Array<any>;
  public ActiveParentRoute: string;
  public ActiveUrl: string;
  public Priorities: TypedPriorityMaster[] = [];
  private imageUtil = new ImageUtil();
  constructor(public apiService: ApiService) {
    super();
    this.BaseURL = AppSettings.URL_Masters;
  }

  setCurrentLoggedInUser(): Promise<any> {
    return this.apiService
      .getAPI(AppSettings.URL_Users, 'Staff/Staff_BasicInfo_Self')
      .then((data) => {
        if (!data) {
          return data;
        }
        data.AppFeatures = data.AppFeatures
          ? mapValues(data.AppFeatures, (features) => {
              const vm = new PropertyAppFeatures();
              vm.AdvanceSecurity = features.AdvanceSecurity === '1';
              vm.WebAuthExpiry = features.WebAuthExpiry === '1';
              vm.CheckOutDaysLimit = toOptionalNumber(
                features.CheckOutDaysLimit
              );
              vm.PreArrivalDaysLimit = toOptionalNumber(
                features.PreArrivalDaysLimit
              );
              vm.MINIBAR_VOID_ALLOWED =
                features.MINIBAR_VOID_ALLOWED === 'TRUE';
              vm.MINIBAR_VOID_DURATION_SECS =
                features.MINIBAR_VOID_DURATION_SECS;
              vm.MinibarHopTime = toOptionalNumber(features.MinibarHopTime);
              vm.MinibarSameRoomRepostTime = toOptionalNumber(
                features.MinibarSameRoomRepostTime
              );
              vm.MinibarShowRepostAlert =
                features.MinibarShowRepostAlert === 'TRUE';
              vm.MinibarShowSeekBar = features.MinibarShowSeekBar === 'TRUE';
              vm.MinibarTotalSeekTime = toOptionalNumber(
                features.MinibarTotalSeekTime
              );
              return vm;
            })
          : {};
        return data;
      });
  }

  getAllPropertyMaster(
    LanguageCode: string,
    PropertyID?: number
  ): Promise<any> {
    LanguageCode = LanguageCode || 'ENG';
    return this.apiService
      .getAPI(
        AppSettings.URL_Masters,
        'API/GetAllPropertyMaster?PropertyID=' +
          this.URLEncode(PropertyID) +
          '&LanguageCode=' +
          this.URLEncode(LanguageCode)
      )
      .then((data) => {
        return data;
      });
  }

  getAllStaticMaster(): any {
    if (this.Priorities?.length) return this.Priorities;
    return this.apiService
      .getAPI(AppSettings.URL_Masters, 'API/GetAllStaticMasters')
      .then((data) => {
        this.Priorities = data?.Priority ?? [];
        return this.Priorities;
      });
  }

  RefreshToken() {
    if (!AppSettings.EnableUnifocusSSO) {
      return Promise.resolve();
    }
    return this.apiService
      .postAPI(AppSettings.URL_Session, 'SSO/Staff_EnsureSession', {DeviceType: 'SSO_WEB'})
      .then((data) => {
        console.log(data);
        if (!data || data.HasError || !data.Result || !data.Result.SessionId) {
          return;
        }
        const sessionId = data.Result.SessionId;
        this.apiService.setCookie('SessionId', sessionId)
      });
  }

  getMainMasters(): Promise<KMTMasterResponse> {
    return this.apiService
      .postAPI(
        AppSettings.URL_KMTMasters,
        'MainMaster/MainMasters_Get',
        {},
        false
      )
      .then((data) => {
        return data;
      });
  }

  getExtendedMasters(LanguageCode: string): Promise<KMTMastersExtDTOResponse> {
    return this.apiService.getAPI(
      AppSettings.URL_KMTMastersV2,
      `KMTMaster/GetKMTMasters?lang=${LanguageCode}`
    );
  }

  LocationStatus_Get(requestObj: LocationStatusGet): Promise<SimpleResponse> {
    return this.apiService
      .postAPI(AppSettings.URL_Masters, 'API/LocationStatus_Get', requestObj)
      .then((data) => {
        return data;
      });
  }

  getExtendedMastersML(
    entity: TranslatedEntities,
    PropertyId: number,
    LangCodes: string[],
    lookupRequest?: WOTypeMLRequest
  ): Promise<SimpleResponse<MLEntityResponse>> {
    const controller = this.getterServiceURLForML(entity);

    if (!PropertyId || !controller) {
      return Promise.resolve(null);
    }
    if (!LangCodes || !LangCodes.length) {
      return Promise.resolve(null);
    }

    let req: any = {
      PropertyId,
      LangCodes,
    };

    if (lookupRequest?.Filter) req.Filter = lookupRequest?.Filter;
    if (lookupRequest?.SearchParam)
      req.SearchParam = lookupRequest?.SearchParam;

    return this.apiService.postAPI(
      AppSettings.URL_KMTMastersV2,
      controller,
      req
    );
  }

  getterServiceURLForML(entity: TranslatedEntities): string | null {
    switch (entity) {
      case TranslatedEntities.WOType:
        return 'KMTMaster/GetWorkOrderTypeML';
      case TranslatedEntities.Reason:
        return 'KMTMaster/GetReasonsML';
      case TranslatedEntities.WODescription:
        return 'KMTMaster/GetWorkOrderDescriptionsML';
      default:
        return null;
    }
  }

  getterMaxLength(entity: TranslatedEntities): number {
    switch (entity) {
      case TranslatedEntities.WOType:
        return 50;
      case TranslatedEntities.Reason:
        return 50;
      case TranslatedEntities.WODescription:
        return 500;
      default:
        return 50;
    }
  }

  setExtendedMastersML(
    entity: TranslatedEntities,
    PropertyId: number,
    ListMLEntities: MLEntity[]
  ): Promise<SimpleResponse> {
    const controller = this.setterServiceURLForML(entity);
    if (!PropertyId || !controller) {
      return Promise.resolve(null);
    }
    if (!ListMLEntities || !ListMLEntities.length) {
      return Promise.resolve(null);
    }

    return this.apiService.postAPI(AppSettings.URL_KMTMastersV2, controller, {
      PropertyId,
      ListMLEntities,
    });
  }

  setterServiceURLForML(entity: TranslatedEntities): string | null {
    switch (entity) {
      case TranslatedEntities.WOType:
        return 'KMTMaster/UpdateWorkOrderTypeML';
      case TranslatedEntities.Reason:
        return 'KMTMaster/UpdateReasonsML';
      case TranslatedEntities.WODescription:
        return 'KMTMaster/UpdateWorkOrderDescriptionML';
      default:
        return null;
    }
  }

  Property_ImageURL(PropertyId: number): string {
    return `${AppSettings.URL_Masters}API/Property_Image?PropertyId=${PropertyId}`;
  }

  App_ImageURL(): string {
    return `${window.location.origin}/assets/images/kmt-logo-v2.png`;
  }

  Property_Image(PropertyId: number, trackLoading = true): Promise<String> {
    const emptyImage = this.EmptyImage();
    const url = `${AppSettings.URL_Masters}API/Property_Image?PropertyId=${PropertyId}`;
    return new Promise((resolve) => {
      this.apiService
        .downloadFile(url, trackLoading)
        .then((resp) => {
          if (!(resp instanceof HttpResponse) || resp.status !== 200) {
            resolve(emptyImage);
            return;
          }

          this.imageUtil
            .readAsDataURL(resp.body)
            .then((res) => resolve(res.dataURL))
            .catch(() => resolve(emptyImage));
        })
        .catch(() => resolve(emptyImage));
    });
  }

  EmptyImage(): string {
    return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAAaADAAQAAAABAAAAAQAAAADa6r/EAAAAC0lEQVQIHWNgAAIAAAUAAY27m/MAAAAASUVORK5CYII=';
  }

  App_ImageBase64(): string {
    return APP_IMAGE_BASE64;
  }

  /** @deprecated */
  getPMTypes(PropertyId: number): Promise<SimpleResponse> {
    return this.apiService
      .getAPI(
        AppSettings.URL_KMTMasters,
        'MainMaster/PMTypes_Get?PropertyId=' + this.URLEncode(PropertyId)
      )
      .then((data) => {
        return data;
      });
  }

  /** @deprecated */
  getAssetCategories(PropertyId: number): Promise<SimpleResponse> {
    const Properties = [];
    Properties.push(PropertyId);
    return this.apiService
      .postAPI(
        AppSettings.URL_KMTMasters,
        'MainMaster/AssetCategories_Lookup_Get?Properties=',
        Properties
      )
      .then((data) => {
        // FIXME: update hierarchial view separately
        // TODO: Calculate Level & Path for category

        if (data && !data.HasError) {
          data.Result = this.generateAssetCatTree(data.Result);
        }
        return data;
      });
  }

  /** @deprecated */
  UserGroup_All(propertyId: number) {
    const userReqObj = new UserGroupSearch();
    userReqObj.SendStaff = true;
    userReqObj.Properties.push(propertyId);
    return this.UserGroup_Search(userReqObj);
  }

  /** @deprecated */
  Metrics_All(PropertyId: number): Promise<SimpleResponse> {
    const requestObj = new MetricSearch();
    requestObj.PropertyId = PropertyId;
    return this.Metric_Search(requestObj);
  }

  /** @deprecated */
  generateNestedLevels() {}

  /** @deprecated */
  generateAssetCatTree(arr: Array<AssetCategoryLookup>): Array<any> {
    const flatCategories = (arr || []).map((category) => ({ ...category }));
    const parentElements = (arr || []).filter(
      (category) =>
        !category.ParentId || (category.ParentId && category.ParentId == null)
    );
    let assetCategoryTree = [];
    parentElements.forEach((a) => {
      a.Level = 0;
      a.Path = a.Name;
      assetCategoryTree.push(a);
      assetCategoryTree = this.generateChildElements(
        a,
        assetCategoryTree,
        flatCategories
      );
    });
    return assetCategoryTree;
  }

  private generateChildElements(parent, array, assetCategories): Array<any> {
    const childElements: Array<any> = assetCategories.filter(
      (a) => a.ParentId === parent.AssetCategoryId
    );

    if (childElements.length > 0) {
      childElements.map((a) => {
        a.Level = parent.Level + 1;
        a.Path = parent.Path.concat('.', a.Name);
        array.push(a);
        this.generateChildElements(a, array, assetCategories);
      });
    }
    return array;
  }

  /** @deprecated */
  UserGroup_Search(userGroupReq: UserGroupSearch): Promise<SimpleResponse> {
    return this.apiService
      .postAPI(AppSettings.URL_KMT, 'UserGroup/UserGroup_Search', userGroupReq)
      .then((data) => {
        return data;
      });
  }

  /** @deprecated */
  Metric_Search(requestObj: MetricSearch): Promise<SimpleResponse> {
    return this.apiService
      .postAPI(AppSettings.URL_KMTMasters, 'Metric/Metric_Search', requestObj)
      .then((data) => {
        return data;
      });
  }

  /** @deprecated */
  AssetCategory_Update(
    requestobj: AssetCategoryUpdate
  ): Promise<SimpleResponse> {
    return this.apiService
      .postAPI(
        AppSettings.URL_KMTMasters,
        'MainMaster/AssetCategory_Update',
        requestobj
      )
      .then((data) => {
        return data;
      });
  }
}
